import React, { createContext, useCallback,useEffect, useState } from 'react';
import axios from 'axios';
import { JAVA_BACKEND_API_URL, JAVA_BACKEND_USER_PASS, getAuthHeader } from "./BackendConstants";
import { fuarService } from "../../services/fuarService";
import { isFirmaAccount, clearFirmaIdCache } from "../../services/firmaSessionService";
import FreeFormFill from "../../pages/FdsSaha/FreeFormFill";
import ProjectSustainabilityForm from "../../pages/Management/Projects/ProjectSustainabilityForm";
import i18n from "../../i18n";

const MainContext = createContext();

// Fallback fuar ID - API çalışmazsa kullanılır. Backlog #187: yalnızca personel için — firma
// hesabı için neredeyse kesin firmanın fuarlarından biri değil (bkz. resolveFirmaFuarId).
const FALLBACK_FUAR_ID = 3;

// Backlog #187: a firma account may only have one of its company's fairs selected
// (GET /fuar/selectable; PUT /users/updateUsersActiveFuar answers 403 for anything else).
// `currentFuarId` is what /fuar/activefuarbyuserid said (null on its 404, i.e. user.fuar_id unset).
// Kept when it is one of the company's fairs; otherwise the newest of them is selected AND saved,
// so the next login finds it. Resolves null when the company has no fair at all.
async function resolveFirmaFuarId(currentFuarId) {
    let selectableIds;
    try {
        selectableIds = (await fuarService.getSelectableFuarList()).map(f => f.fuarId);
    } catch (error) {
        // Can't check the company's list: keep the backend's own answer (or the last pick on this
        // browser), but never FALLBACK_FUAR_ID.
        console.warn("fuar/selectable API hatası:", error.message);
        const saved = parseInt(localStorage.getItem('activeFairId'));
        return currentFuarId || (Number.isFinite(saved) ? saved : null);
    }
    if (currentFuarId && selectableIds.includes(currentFuarId)) return currentFuarId;

    const newest = selectableIds[0] ?? null;
    if (newest) {
        try {
            await fuarService.setActiveFuar(newest);
        } catch (error) {
            console.warn("Firma fuarı kaydedilemedi:", error.message);
        }
    }
    return newest;
}

export const MainProvider = ({ children }) => {
    const [selectedFuarId, setSelectedFuarId] = useState(null);
    //const [activeFairId, setActiveFairId] = useState(null);
    const [menuItemClicked, setMenuItemClicked] = useState(null);
    const [fuarZamani, setfuarZamani] = useState(null);
    const defaultVisibileItems = ['Dashboard', 'SahaModulu', 'BetterStandsNew', 'BetterStands', 'Management', 'Fuarlar', 'Firmalar', 'SalonlarKrokiler', 'Kisi', 'Standlar', 'FdsIstatistikler', 'FreeForm', 'FormYonetimi', 'AnaSayfa', 'OzelFormlar', 'Projeler', 'ProjectSustainabilityForm', 'Kroki', 'SistemAyarlari', 'GlobalKurallar', 'EmailMenusu'];   // #585: the E-posta menu (its items follow their routes' permissions)
    // Firma (grup 9: Firma Moderatör, grup 19: Firma Admin) girişinde soldaki menüde
    // sadece Ana Sayfa, Firma Kartı, Standlarım, Talepler, Projeler, Personel ve Taşeronlar
    // görünür — diğer tüm iç modüller gizlenir.
    // 'FirmaTaseronlar' (firma_taseron, dekoratör⇄taşeron) 2026-09-04'te eklendi (2026-09-03
    // backend değişikliğinin firma tarafındaki karşılığı). 'FirmaPersonelListem' (daimî liste) 2026-10-06'da
    // (#300) kaldırıldı: liste artık Personel sayfasının "Daimî Liste" görünümü.
    // #311 (2026-10-11): + Organizatör Paneli ve Gerekli Belgeler. Bu liste artık yalnızca ADAYLARI veriyor;
    // hangisinin görüneceğine aktif arayüz setinin modülleri karar veriyor (VerticalLayouts, PORTAL_MENU_MODULU).
    // WAS: const firmaVisibleItems = ['FirmaAnaSayfa', 'FirmaKart', 'FirmaStandlar', 'FirmaTalepler', 'FirmaProjeler', 'FirmaPersonel', 'FirmaTaseronlar', 'FirmaKisiKartim'];
    // WAS (until #322): const firmaVisibleItems = ['FirmaAnaSayfa', 'FirmaKart', 'FirmaStandlar', 'FirmaTalepler', 'FirmaProjeler', 'FirmaPersonel', 'FirmaTaseronlar', 'FirmaOrganizator', 'FirmaBelgeGereksinimleri', 'FirmaKisiKartim'];
    // WAS (until #385): ..., 'FirmaKroki', 'FirmaBelgeGereksinimleri', 'FirmaKisiKartim'] — the "Kişi Kartım" prototype left the menu.
    // WAS (until #536): ..., 'FirmaKroki', 'FirmaBelgeGereksinimleri'];
    const firmaVisibleItems = ['FirmaAnaSayfa', 'FirmaKart', 'FirmaStandlar', 'FirmaTalepler', 'FirmaProjeler', 'FirmaPersonel', 'FirmaTaseronlar', 'FirmaOrganizator', 'FirmaKroki', 'FirmaBelgeGereksinimleri', 'FirmaFuarKurallari'];
    // There was an old, unused 'allVisibleItems' constant here that already had 'Users' in it —
    // but it had drifted out of sync with defaultVisibileItems (missing 'Kroki', at least, and now
    // 'Fuarlar'/'SalonlarKrokiler' too), so using it as-is for groups 1/2 would have silently
    // dropped menu items for them. Deriving from defaultVisibileItems instead guarantees admins
    // keep everything everyone else has, plus Kullanıcılar (backlog #13 — admin-only, since
    // UserController has almost no backend auth).
    // 'PlanAyarlari' (backlog #243, Sistem Ayarları › Plan Ayarları): route roles [1,2], and writes need
    // SYSTEM_SETTINGS_MANAGE on the backend — shown to admins only.
    // WAS: const adminVisibleItems = [...defaultVisibileItems, 'Users'];
    // WAS (until 2026-10-10): const adminVisibleItems = [...defaultVisibileItems, 'Users', 'PlanAyarlari'];
    // Backlog #160: Sistem Ayarları › E-posta Hesapları / Şablonları / Kayıtları — admins only in the menu
    // (mailbox writes need SYSTEM_SETTINGS_MANAGE on the backend; the routes stay roles [1,2,3]).
    // WAS (until #159): const adminVisibleItems = [...defaultVisibileItems, 'Users', 'PlanAyarlari', 'EmailHesaplari', 'EmailSablonlari', 'EmailKayitlari'];
    // WAS (until #158): const adminVisibleItems = [...defaultVisibileItems, 'Users', 'PlanAyarlari', 'EmailHesaplari', 'EmailSablonlari', 'EmailKayitlari', 'FirmaTurleri'];
    // WAS (until #162): const adminVisibleItems = [...defaultVisibileItems, 'Users', 'PlanAyarlari', 'EmailHesaplari', 'EmailSablonlari', 'EmailKayitlari', 'FirmaTurleri', 'DosyaTurleri'];
    const adminVisibleItems = [...defaultVisibileItems, 'Users', 'PlanAyarlari', 'EmailHesaplari', 'EmailSablonlari', 'EmailKayitlari', 'FirmaTurleri', 'DosyaTurleri', 'DosyaAyarlari'];
    // 'YetkiYonetimi' (backlog #6, Sistem Ayarları › Yetki Yönetimi): ONLY the root user (group 1) —
    // route roles [1], and RbacController refuses everyone else (403) on its own.
    // #309 (2026-10-10): also shown to anyone holding rbac.manage (VerticalLayout reads GET /rbac/me);
    // the route opens by that permission and RbacController checks it on every call.
    const rootVisibleItems = [...adminVisibleItems, 'YetkiYonetimi'];
    const api = {
        Login: `${JAVA_BACKEND_API_URL}/login/request`,
        User: `${JAVA_BACKEND_API_URL}/users/`,
        Group: `${JAVA_BACKEND_API_URL}/users/user/`,
        groups: {
            1: {
                // WAS: visibleItems: adminVisibleItems,
                visibleItems: rootVisibleItems,
                roles: [1],
            },
            2: {
                visibleItems: adminVisibleItems,
                roles: [2],
            },
            3: {
                visibleItems: defaultVisibileItems,
                roles: [3],
            },
            4: {
                visibleItems: defaultVisibileItems,
                roles: [4],
            },
            5: {
                visibleItems: defaultVisibileItems,
                roles: [5],
            },
            6: {
                visibleItems: defaultVisibileItems,
                roles: [6],
            },
            7: {
                visibleItems: defaultVisibileItems,
                roles: [7],
            },
            8: {
                visibleItems: defaultVisibileItems,
                roles: [8],
            },
            9: {
                visibleItems: firmaVisibleItems,
                roles: [9],
            },
            10: {
                visibleItems: defaultVisibileItems,
                roles: [10],
            },
            11: {
                visibleItems: defaultVisibileItems,
                roles: [11],
            },
            12: {
                visibleItems: defaultVisibileItems,
                roles: [12],
            },
            13: {
                visibleItems: defaultVisibileItems,
                roles: [13],
            },
            14: {
                visibleItems: defaultVisibileItems,
                roles: [14],
            },
            15: {
                visibleItems: defaultVisibileItems,
                roles: [15],
            },
            16: {
                visibleItems: defaultVisibileItems,
                roles: [16],
            },
            17: {
                visibleItems: defaultVisibileItems,
                roles: [17],
            },
            18: {
                visibleItems: defaultVisibileItems,
                roles: [18],
            },
            19: {
                visibleItems: firmaVisibleItems,
                roles: [19],
            },
            // #307 (2026-10-10): the menu of EVERY company-side login (not staff by the backend's rule:
            // 9, 19, the Organizatör roles 14-18, any company role added later). VerticalLayout picks it
            // from groupInfo.staff, so the 14-18 entries above are no longer read.
            firma: {
                visibleItems: firmaVisibleItems,
                roles: []
            },
            default: {
                visibleItems: defaultVisibileItems,
                roles: []
            }
        }
    };
    const fetchActiveFairId = useCallback(async () => {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');

        if (!userId || !token) {
            console.warn("fetchActiveFairId: userId veya token yok");
            const saved = localStorage.getItem('activeFairId');
            if (saved) {
                setSelectedFuarId(parseInt(saved));
            }
            return;
        }

        let fuarId = null;
        try {
            const response = await axios.get(
                `${JAVA_BACKEND_API_URL}/fuar/activefuarbyuserid/${userId}`,
                { headers: getAuthHeader() }
            );

            fuarId = response.data?.id || null;
            if (fuarId) {
                console.log("✅ Backend'den aktif fuar geldi:", fuarId);
            } else {
                throw new Error(i18n.t("components:mainContext.fuarIdBosGeldi"));
            }
        } catch (error) {
            console.warn("fetchActiveFairId API hatası:", error.message);
        }

        // Backlog #187: the firma-side fallback is its newest own fair, not activeFairId/FALLBACK_FUAR_ID.
        if (isFirmaAccount()) {
            const firmaFuarId = await resolveFirmaFuarId(fuarId);
            setSelectedFuarId(firmaFuarId);
            if (firmaFuarId) localStorage.setItem('activeFairId', String(firmaFuarId));
            else localStorage.removeItem('activeFairId');
            return;
        }

        if (fuarId) {
            setSelectedFuarId(fuarId); // ← KRITIK: setSelectedFuarId çağır
            localStorage.setItem('activeFairId', String(fuarId));
        } else {
            const saved = localStorage.getItem('activeFairId');
            const fallbackId = saved ? parseInt(saved) : FALLBACK_FUAR_ID;
            setSelectedFuarId(fallbackId); // ← localStorage'dan da atanması gerekir
            localStorage.setItem('activeFairId', String(fallbackId));
        }
    }, []);


/* const fetchActiveFairId = useCallback(async () => {
     const userId = localStorage.getItem('userId');
     const token = localStorage.getItem('token');

     // Kullanıcı giriş yapmamışsa veya token yoksa çık
     if (!userId || !token) {
         console.warn("fetchActiveFairId: userId veya token yok, giriş yapılmamış.");
         // localStorage'da kayıtlı fuar var mı bak
         const saved = localStorage.getItem('activeFairId');
         if (saved) {
             setSelectedFuarId(parseInt(saved));
         }
         return;
     }

     try {
         const response = await axios.get(
             `${JAVA_BACKEND_API_URL}/fuar/activefuarbyuserid/${userId}`,
             { headers: getAuthHeader() }
         );

         const fuarId = response.data?.id;
         if (fuarId) {
             setSelectedFuarId(fuarId);
             localStorage.setItem('activeFairId', String(fuarId));
             console.log("✅ Aktif fuar yüklendi:", fuarId);
         } else {
             throw new Error("Fuar ID boş geldi");
         }
     } catch (error) {
         console.warn("fetchActiveFairId API hatası, localStorage'dan okunuyor:", error.message);
         const saved = localStorage.getItem('activeFairId');
         const fallbackId = saved ? parseInt(saved) : FALLBACK_FUAR_ID;
         setSelectedFuarId(fallbackId);
         localStorage.setItem('activeFairId', String(fallbackId));
     }
 }, []); */


 useEffect(() => {
     async function run() { await fetchActiveFairId(); }
     run();
     // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []); // sadece uygulama ilk açıldığında çalış


 // #640: the session setup of a successful login (the id and token are stored, the user, the group and the fair are read), shared by the
 // password login and by the MFA second step. The text is the inline block handleLoginContext had until #640, unchanged.
 const oturumuKur = useCallback(async (userId, token) => {
     localStorage.setItem('userId', userId);
     localStorage.setItem('token', token);

     // Backlog #301: GET /users/{id} and /users/user/{id}/group now need the session
     // (X-user-id + X-token, just stored above) — Basic auth alone answers 401.
     // WAS: headers: { 'Authorization': authHeader } on both calls below.
     const userResponse = await axios.get(
         `${api.User}${userId}`,
         {
             headers: getAuthHeader()
         }
     );
     localStorage.setItem('user', JSON.stringify(userResponse.data));

     const groupResponse = await axios.get(
         `${api.Group}${userId}/group`,
         {
             headers: getAuthHeader()
         }
     );
     localStorage.setItem('groupInfo', JSON.stringify(groupResponse.data));

     // Backlog #187: the mount-once effect above ran on the login page, before anyone was
     // logged in, so nothing selected this user's fair until a full reload. Resolve it here,
     // before Login navigates on. The previous account's cached firma id goes too.
     clearFirmaIdCache();
     await fetchActiveFairId();
 }, [api.User, api.Group, fetchActiveFairId]);

 const handleLoginContext = useCallback(async (username, password) => {
     const authHeader = 'Basic ' + btoa(JAVA_BACKEND_USER_PASS);

     try {
         const formData = new FormData();
         formData.append('user_login_name', username);
         formData.append('user_login_password', password);

         const response = await axios.post(
             api.Login,
             formData,
             {
                 headers: {
                     'Content-Type': 'multipart/form-data',
                     'Authorization': authHeader
                 }
             }
         );

         if (response.data.result) {
             // #640: a user with a second factor gets mfa_required + login_ticket and NO token yet; the login page shows the code screen and
             // finishes with handleMfaLoginContext (same session setup, below) once /login/mfa/verify has answered with the token.
             if (response.data.mfa_required) {
                 return { mfaRequired: true, challenge: response.data };
             }
             await oturumuKur(response.data.user_id, response.data.token);
             return true;
             // WAS (until #640): the session setup written inline here, now oturumuKur() above:
             // localStorage.setItem('userId', response.data.user_id);
             // localStorage.setItem('token', response.data.token);
             //
             // const userId = response.data.user_id;

             // Backlog #301: GET /users/{id} and /users/user/{id}/group now need the session
             // (X-user-id + X-token, just stored above) — Basic auth alone answers 401.
             // WAS: headers: { 'Authorization': authHeader } on both calls below.
             // const userResponse = await axios.get(
                 // `${api.User}${userId}`,
                 // {
                     // headers: getAuthHeader()
                 // }
             // );
             // localStorage.setItem('user', JSON.stringify(userResponse.data));

             // const groupResponse = await axios.get(
                 // `${api.Group}${userId}/group`,
                 // {
                     // headers: getAuthHeader()
                 // }
             // );
             // localStorage.setItem('groupInfo', JSON.stringify(groupResponse.data));

             // Backlog #187: the mount-once effect above ran on the login page, before anyone was
             // logged in, so nothing selected this user's fair until a full reload. Resolve it here,
             // before Login navigates on. The previous account's cached firma id goes too.
             // clearFirmaIdCache();
             // await fetchActiveFairId();

             // Sayfayı yenile
             // return true;
         } else {
             throw new Error('Login failed');
         }
     } catch (error) {
         console.error('Login error:', error);
         throw error;
     }
 }, [api.Login, oturumuKur]);
 // WAS (until #640): }, [api.Login, api.User, api.Group, fetchActiveFairId]);


 const getUserDataContext = useCallback(async () => {
     const userId = localStorage.getItem('userId');
     const token = localStorage.getItem('token');
     // Backlog #301: both reads need the session now — getAuthHeader() carries X-user-id + X-token.
     // WAS: const authHeader = 'Basic ' + btoa(JAVA_BACKEND_USER_PASS);
     //      ... headers: { Authorization: authHeader } on both calls below.

     if (userId && token) {
         try {
             const userResponse = await axios.get(`${api.User}${userId}`, {
                 headers: getAuthHeader(),
             });
             const groupResponse = await axios.get(`${api.Group}${userId}/group`, {
                 headers: getAuthHeader(),
             });
             return { userInfo: userResponse.data, groupInfo: groupResponse.data };
         } catch (error) {
             console.error('Error fetching user info:', error);
             throw error;
         }
     } else {
         throw new Error('User is not logged in');
     }
 }, [api.User, api.Group]);

 const getUserGroupContext = useCallback(async (userId) => {
     // Önce localStorage'dan oku
     const saved = localStorage.getItem('groupInfo');
     if (saved) return JSON.parse(saved);

     // Backlog #301: needs the session now (getAuthHeader). WAS:
     // const authHeader = 'Basic ' + btoa(JAVA_BACKEND_USER_PASS);
     // ... headers: { Authorization: authHeader }
     try {
         const response = await axios.get(`${api.Group}${userId}/group`, {
             headers: getAuthHeader()
         });
         return response.data;
     } catch (error) {
         console.error('Error fetching user group:', error);
         // #301: a failed lookup used to fall back to group 1 — the ROOT menu (Yetki Yönetimi,
         // Kullanıcılar, ...) for whoever's request failed, e.g. on an expired session. null makes
         // VerticalLayouts use api.groups.default instead; the backend refuses those pages anyway.
         // WAS: return { id: 1, userGroupEntity: { id: 1 } };
         return null;
     }
 }, [api.Group]);


 return (
     <MainContext.Provider value={{ handleLoginContext, handleMfaLoginContext: oturumuKur, getUserDataContext, getUserGroupContext, api,  selectedFuarId,
         setSelectedFuarId,fuarZamani,setfuarZamani , fetchActiveFairId, menuItemClicked, setMenuItemClicked}}>
         {children}
     </MainContext.Provider>
 );
};

export default MainContext;
